/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next v17 requires TypeScript 5+; this project is on TS 4.9.5.
// See src/i18n/index.ts for the project-wide workaround.
import { useState } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiService, AvailableProfileDto, LoginV2Response, SwitchProfileResponse } from '../services/api';
import { normalizeApiError } from '../types/api-errors';
import { ProfileType } from '../types/enums';
import { useAuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isAuthV2Enabled } from '../lib/auth/authV2Flag';
import { postLoginPath } from '../lib/auth/returnPath';
import { resetClientStateForProfileSwitch } from '../lib/auth/resetClientStateForProfileSwitch';
import { claimAccessRemovedToast, isStaffAccessRemoved, STAFF_ACCESS_REMOVED_KEY } from '../lib/auth/staffAccess';
import { useOptionalLanguage } from '../contexts/LanguageContext';

const useTranslation = useTranslationUntyped as any;

interface LoginRequest {
    identifier: string;
    password: string;
    userType: ProfileType;
}

/**
 * Auth v2 login request — no `userType`. Server resolves profiles and returns
 * either an active profile + full token, OR availableProfiles + chooser-pending
 * token (when multi-profile and no LastActiveProfileId).
 */
export interface LoginV2Request {
    identifier: string;
    password: string;
}

export interface RegisterRequest {
    email: string;
    password: string;
    fullName: string;
    termsAccepted: boolean;
    userType: ProfileType;
}

function parseJwtUserType(token: string): number | null {
    try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        const val = decoded['userType'];
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const n = parseInt(val, 10);
            if (!isNaN(n)) return n;
        }
        return null;
    } catch {
        return null;
    }
}

export const useAuth = () => {
    const [loading, setLoading] = useState(false);
    const { setTokens, setEmailVerificationRequired, setPendingVerificationEmail } = useAuthContext();
    const { showToast } = useToast();
    // S1 (AB#2204, SG-11) — a staff member whose salon access was removed is told
    // so (localized) instead of "wrong password".
    const language = useOptionalLanguage();
    const accessRemovedMessage = () => language?.t(STAFF_ACCESS_REMOVED_KEY) ?? STAFF_ACCESS_REMOVED_KEY;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t } = useTranslation('auth');

    const refreshAccessToken = async (): Promise<boolean> => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) return false;
        try {
            const response = await apiService.refreshAccessToken(refreshToken);
            const data = response.data as { accessToken?: string; refreshToken?: string };
            if (data?.accessToken && data?.refreshToken) {
                apiService.setTokens(data.accessToken, data.refreshToken);
                setTokens(data.accessToken, data.refreshToken);
                return true;
            }
            return false;
        } catch {
            return false;
        }
    };

    const login = async (data: LoginRequest) => {
        setLoading(true);
        try {
            const response = await apiService.login(data);
            if (response.accessToken && response.refreshToken) {
                const jwtType = parseJwtUserType(response.accessToken);
                if (jwtType !== null && jwtType !== data.userType) {
                    showToast('This account does not match the selected role. Please choose the correct role and try again.', 'error');
                    setLoading(false);
                    return;
                }
                setTokens(response.accessToken, response.refreshToken);
                showToast('Successfully logged in', 'success');
                navigate(postLoginPath(), { replace: true }); // AB#2201 — back to where the session ended
            }
            return response;
        } catch (err: unknown) {
            // AB#1919 (WS-5b) — status + human message via the single normalizer
            // (status prefers 7807 `status`; detail prefers 7807 `detail`).
            const appError = normalizeApiError(err);
            const status = appError.status;
            const serverMessage = appError.detail.toLowerCase();
            let message: string;

            if (isStaffAccessRemoved(err)) {
                message = accessRemovedMessage();
            } else if (
                status === 401 ||
                status === 404 ||
                serverMessage.includes('password') ||
                serverMessage.includes('invalid credentials') ||
                serverMessage.includes('not found') ||
                serverMessage.includes('no account')
            ) {
                // Security (OWASP OAT-010): Do not differentiate between
                // "no account" (404) and "wrong password" (401). Both must
                // return the same ambiguous message to prevent account
                // enumeration.
                message = 'The email or password you entered is incorrect.';
            } else if (status === 423 || serverMessage.includes('locked') || serverMessage.includes('blocked')) {
                message = 'Your account has been locked. Please contact support or try again later.';
            } else if (status === 429 || serverMessage.includes('too many')) {
                message = 'Too many login attempts. Please wait a few minutes and try again.';
            } else if (appError.detail) {
                message = appError.detail;
            } else {
                message = 'Unable to connect. Please check your internet connection and try again.';
            }

            // D2 — a removed staff member is told inline by the login form; no
            // second (stacked) toast on top of the session-end one.
            if (!isStaffAccessRemoved(err)) showToast(message, 'error');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const register = async (data: RegisterRequest) => {
        setLoading(true);
        try {
            await apiService.register({
                ...data,
                userType: data.userType ?? ProfileType.SalonOwner
            });
            return;
        } catch (err: unknown) {
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Auth v2 login (AB#1434). Calls POST /v2/auth/login. Handles three paths:
     *   1. Single profile → activeProfileId set, full claims in token → navigate home.
     *   2. Multi-profile with LastActiveProfileId → same as (1).
     *   3. Multi-profile + no prior selection → activeProfileId = null; caller
     *      is expected to show a chooser screen and call {@link useAuth.switchProfile}
     *      with the user's pick.
     *
     * In all three cases this function stores the tokens and resets client state
     * so the NotificationsContext / SignalR etc. pick up the new auth.
     */
    const loginV2 = async (data: LoginV2Request): Promise<LoginV2Response | undefined> => {
        setLoading(true);
        try {
            const response = await apiService.loginV2(data);

            if (response.accessToken && response.refreshToken) {
                setTokens(response.accessToken, response.refreshToken);

                // AB#1612 Phase 1A.2 — capture the soft-gate flag from the
                // login response so the global EmailVerificationBanner has
                // something to render. Email is the identifier when login
                // happened via email; we stash it for the "we sent a link
                // to <email>" copy. Phone-based logins clear both.
                const wasEmailLogin = data.identifier.includes('@');
                if (response.emailVerificationRequired && wasEmailLogin) {
                    setEmailVerificationRequired(true);
                    setPendingVerificationEmail(data.identifier);
                } else {
                    setEmailVerificationRequired(false);
                    setPendingVerificationEmail(null);
                }

                // Case 1 + 2: active profile resolved — full navigate.
                if (response.activeProfileId != null) {
                    resetClientStateForProfileSwitch(queryClient, {
                        newAccessToken: response.accessToken,
                        newRefreshToken: response.refreshToken,
                        activeProfileId: response.activeProfileId,
                        source: 'login',
                    });
                    showToast(t('login.submit'), 'success');
                    navigate(postLoginPath(), { replace: true }); // AB#2201 — back to where the session ended
                }
                // Case 3: chooser pending — caller owns the UX. Tokens are
                // already stored; the chooser screen calls switchProfile next.
            }

            return response;
        } catch (err: unknown) {
            const axErr = err as { response?: { status?: number; data?: { message?: string } } };
            const status = axErr?.response?.status;

            let msg: string;
            if (isStaffAccessRemoved(err)) {
                msg = accessRemovedMessage();
            } else if (status === 401 || status === 404) {
                msg = t('login.errors.invalidCredentials');
            } else if (status === 423) {
                msg = t('login.errors.locked');
            } else if (status === 429) {
                msg = t('login.errors.rateLimited');
            } else {
                msg = t('login.errors.network');
            }
            // D2 — the login form shows the access-removed message inline.
            if (!isStaffAccessRemoved(err)) showToast(msg, 'error');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Auth v2 switch-profile (AB#1435 consumer). Mints a new access token scoped
     * to the target profile and resets client-side caches so the new tenant's
     * data is visible immediately. Navigates home on success.
     *
     * Returns `{ needsElevation: true }` when the server responds with 403
     * Elevation (switching to SuperAdmin without fresh `passwordVerifiedAt`);
     * the caller shows the elevation modal, calls {@link verifyPasswordV2},
     * then retries.
     */
    const switchProfileV2 = async (profileId: number): Promise<SwitchProfileResponse | { needsElevation: true } | undefined> => {
        setLoading(true);
        try {
            const response = await apiService.switchProfile(profileId);
            if (response.accessToken) {
                resetClientStateForProfileSwitch(queryClient, {
                    newAccessToken: response.accessToken,
                    activeProfileId: response.activeProfileId,
                    source: 'switch-profile',
                });
                // AB#2201 — after the post-login chooser, honour returnTo too (absent
                // on an in-portal profile switch, where this falls back to '/').
                navigate(postLoginPath(), { replace: true });
            }
            return response;
        } catch (err: unknown) {
            const axErr = err as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } };
            const status = axErr?.response?.status;
            const errors = axErr?.response?.data?.errors ?? {};

            // Elevation required — don't toast; let the caller show the modal.
            if (status === 403 && 'Elevation' in errors) {
                return { needsElevation: true };
            }

            if (isStaffAccessRemoved(err)) {
                // D2 — at most one access-removed toast per removal.
                if (claimAccessRemovedToast()) showToast(accessRemovedMessage(), 'error');
            } else if (status === 429) {
                showToast(t('login.errors.rateLimited'), 'error');
            } else {
                showToast(t('toast.switchFailed'), 'error');
            }
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Auth v2 verify-password (AB#1437 consumer). Used before switching to a
     * SuperAdmin profile. On success, updates the access token with a fresh
     * `passwordVerifiedAt` claim so the next switchProfile call passes the
     * elevation check.
     */
    const verifyPasswordV2 = async (password: string): Promise<boolean> => {
        setLoading(true);
        try {
            const response = await apiService.verifyPassword(password);
            return Boolean(response.accessToken);
        } catch {
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Auth v2 refresh of the available-profiles list. Used by the switcher on
     * 401 or suspected cache staleness (AB#1435 / AB#1438).
     */
    const refreshProfilesV2 = async (): Promise<AvailableProfileDto[]> => {
        try {
            return await apiService.getProfilesV2();
        } catch {
            return [];
        }
    };

    const socialLogin = async (provider: number, idToken: string, userType: ProfileType, termsAccepted = true) => {
        setLoading(true);
        try {
            const response = await apiService.socialAuth({ provider, idToken, userType, termsAccepted });
            if (response?.accessToken && response?.refreshToken) {
                setTokens(response.accessToken, response.refreshToken);
                showToast('Successfully logged in', 'success');
                navigate(postLoginPath(), { replace: true }); // AB#2201 — back to where the session ended
            }
            return response;
        } catch (err: unknown) {
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        login,
        register,
        socialLogin,
        loading,
        refreshAccessToken,
        // Auth v2
        loginV2,
        switchProfileV2,
        verifyPasswordV2,
        refreshProfilesV2,
        isAuthV2Enabled: isAuthV2Enabled(),
    };
};
